import React from 'react';
import { GapSizeType } from '../../../theme/type/gap-type';
import { BreakpointSizeType } from '../../../theme/type/breakpoints-type';
import { TextSizeType, TextVariantType } from '../../../theme/type/text-type';
import {
    alignClass,
    breakpointPrefix,
    cn,
    marginBottomClass,
    marginLeftClass,
    marginRightClass,
    marginTopClass,
    textSizeClass,
    textVariantClass,
} from '../../../utils/tailwind';

const getResponsiveSizeClasses = (size: Partial<Record<BreakpointSizeType, TextSizeType>>) => {
    const entries = Object.entries(size) as [BreakpointSizeType, TextSizeType][];

    return entries.map(([breakpoint, value], index) => {
        const responsiveClass = textSizeClass[value];

        return index === 0 ? responsiveClass : `${breakpointPrefix[breakpoint]}${responsiveClass}`;
    });
};

const getResponsiveAlignClasses = (align: Partial<Record<BreakpointSizeType, string>>) => {
    const entries = Object.entries(align) as [BreakpointSizeType, string][];

    return entries.map(([breakpoint, value], index) => {
        const responsiveClass = alignClass(value);

        return index === 0 ? responsiveClass : `${breakpointPrefix[breakpoint]}${responsiveClass}`;
    });
};

interface TextPropsInterface {
    tag?: React.ElementType | keyof JSX.IntrinsicElements;
    gap?: GapSizeType;
    marginTop?: GapSizeType;
    marginLeft?: GapSizeType;
    marginRight?: GapSizeType;
    marginBottom?: GapSizeType;
    children: React.ReactNode;
    align?: string | object;
    variant?: TextVariantType;
    size?: TextSizeType | object;
    bold?: boolean;
    semiBold?: boolean;
    extraBold?: boolean;
    italic?: boolean;
    uppercase?: boolean;
    nowrap?: boolean;
}

export const Text: React.FC<TextPropsInterface> = ({
    children,
    tag,
    variant = 'default',
    gap = 'none',
    marginTop = 'none',
    marginBottom = 'none',
    marginLeft = 'none',
    marginRight = 'none',
    size = 'medium',
    align,
    bold,
    semiBold,
    extraBold,
    italic,
    uppercase,
    nowrap,
}) => {
    let alignClasses = '';

    if (typeof align === 'string') {
        alignClasses = alignClass(align);
    } else if (typeof align === 'object') {
        alignClasses = getResponsiveAlignClasses(align).join(' ');
    }

    const sizeClasses = typeof size === 'string' ? textSizeClass[size] : getResponsiveSizeClasses(size).join(' ');
    const hasCustomFontWeight = semiBold || extraBold || bold;

    const classes = cn(
        'm-0',
        !hasCustomFontWeight && 'font-normal',
        marginBottom === 'none' ? marginBottomClass[gap] : '',
        marginTopClass[marginTop],
        marginBottom !== 'none' && marginBottomClass[marginBottom],
        marginLeftClass[marginLeft],
        marginRightClass[marginRight],
        sizeClasses,
        alignClasses,
        variant ? textVariantClass[variant] : '',
        semiBold && 'font-medium',
        extraBold && 'font-black',
        bold && 'font-bold',
        italic && 'italic',
        uppercase && 'uppercase',
        nowrap && 'whitespace-nowrap',
    );

    const Tag = tag || 'p';

    return <Tag className={classes}>{children}</Tag>;
};
